/**
 * Party size selection step for custom request flow.
 * Large centered number with +/- stepper buttons (Executive-style).
 * @module Widget/CustomRequest/steps/PartySizeStep
 */

import React from 'react';
import styles from '../CustomRequest.module.css';

interface PartySizeStepProps {
  value: number;
  onChange: (size: number) => void;
  minSize: number;
  maxSize: number;
  responseTimeText?: string | null;
}

/**
 * Numeric stepper for selecting party size within restaurant min/max bounds.
 * Shows response time info when available from the API.
 *
 * @param props - Component props
 * @returns Rendered party size selector
 */
const PartySizeStep: React.FC<PartySizeStepProps> = ({
  value,
  onChange,
  minSize,
  maxSize,
  responseTimeText,
}) => {
  return (
    <div className="flex flex-col items-center gap-8 py-6">
      {/* Large number + stepper */}
      <div className="flex items-center gap-8">
        <button
          className={styles.stepperButton}
          onClick={() => onChange(Math.max(minSize, value - 1))}
          disabled={value <= minSize}
          aria-label="Decrease party size"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M5 12h14" />
          </svg>
        </button>

        <span className={styles.largeNumber}>{value}</span>

        <button
          className={styles.stepperButton}
          onClick={() => onChange(Math.min(maxSize, value + 1))}
          disabled={value >= maxSize}
          aria-label="Increase party size"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M12 5v14M5 12h14" />
          </svg>
        </button>
      </div>

      {/* Range label */}
      <p className={styles.sectionSubtitle}>
        up to {maxSize} guests
      </p>

      {/* Response time info */}
      {responseTimeText && (
        <div className={styles.infoCard}>
          <p className={styles.centeredTextSmall}>{responseTimeText}</p>
        </div>
      )}
    </div>
  );
};

export default PartySizeStep;
